import React, { useState } from "react";
import { NavLink, useNavigate, Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCompare } from "../context/CompareContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  LayoutDashboard, Table2, GitCompareArrows, Target, Rocket, Bot,
  Database, Scale, SlidersHorizontal, LogOut, RefreshCw, Search, ShieldCheck, X, ChevronRight, Activity,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/explorer", label: "Research Explorer", icon: Table2 },
  { to: "/compare", label: "Comparison", icon: GitCompareArrows },
  { to: "/opportunities", label: "Market Gaps", icon: Target },
  { to: "/design", label: "Design My ICO", icon: Rocket },
  { to: "/assistant", label: "AI Analyst", icon: Bot },
  { to: "/sources", label: "Sources", icon: Database },
  { to: "/jurisdictions", label: "Jurisdictions", icon: Scale },
  { to: "/settings", label: "Settings", icon: SlidersHorizontal },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { ids, clear } = useCompare();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [running, setRunning] = useState(false);

  const runResearch = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/research/run-all");
      toast.success("Daily market scan complete", { description: `${data.scanned} projects re-checked. ${data.note}` });
    } catch (e) {
      toast.error("Research run failed");
    } finally {
      setRunning(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    nav(`/explorer?search=${encodeURIComponent(q)}`);
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-card border-r border-border flex flex-col fixed h-screen z-30">
        <Link to="/" className="flex items-center gap-2.5 px-5 h-16 border-b border-border" data-testid="brand-logo">
          <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-sm">
            <Activity className="w-4.5 h-4.5 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-[15px] tracking-tight text-foreground">TokenScope</div>
            <div className="overline text-muted-foreground text-[9px]">ICO Intelligence</div>
          </div>
        </Link>

        <nav className="flex-1 py-3 overflow-y-auto no-scrollbar">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-2 transition-colors duration-150",
                  isActive
                    ? "border-primary bg-accent text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/60"
                )
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-sm bg-secondary flex items-center justify-center font-heading font-bold text-xs text-secondary-foreground">
              {(user?.email || "A")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate text-foreground">{user?.name || "Admin"}</div>
              <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
            </div>
            <button onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn" className="text-muted-foreground hover:text-destructive transition-colors" title="Log out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-60 flex flex-col min-w-0">
        <header className="h-16 bg-card border-b border-border flex items-center gap-4 px-6 sticky top-0 z-20">
          <form onSubmit={onSearch} className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects, tokens, sectors…"
              data-testid="global-search"
              className="w-full h-9 pl-9 pr-3 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring"
            />
          </form>
          <div className="hidden lg:flex items-center gap-1.5 text-[11px] text-muted-foreground border border-border rounded-sm px-2 py-1">
            <ShieldCheck className="w-3.5 h-3.5" />
            Research only — not investment or legal advice
          </div>
          <button
            onClick={runResearch}
            disabled={running}
            data-testid="run-research-btn"
            className="inline-flex items-center gap-2 h-9 px-3.5 bg-primary text-primary-foreground text-sm font-semibold rounded-sm hover:bg-primary/90 disabled:opacity-60 transition-colors"
          >
            <RefreshCw className={cn("w-4 h-4", running && "animate-spin")} />
            {running ? "Scanning…" : "Run Research"}
          </button>
        </header>

        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>

      {/* Compare tray */}
      {ids.length > 0 && location.pathname !== "/compare" && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 fade-up">
          <div className="flex items-center gap-3 bg-primary text-primary-foreground rounded-sm shadow-xl px-4 py-2.5 border border-white/10">
            <GitCompareArrows className="w-4 h-4" />
            <span className="text-sm font-semibold">{ids.length} selected to compare</span>
            <button onClick={() => nav("/compare")} data-testid="tray-compare-btn" className="inline-flex items-center gap-1 text-sm font-semibold bg-white/15 hover:bg-white/25 rounded-sm px-2.5 py-1 transition-colors">
              Compare <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <button onClick={clear} data-testid="tray-clear-btn" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors" title="Clear">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
